import { supabase } from '@/integrations/supabase/client';
import { getModuleForPath, MODULE_LABELS, ROUTE_MODULE_MAP, type ModuleKey } from '@/hooks/useModulePermissions';

// Uso do sistema por módulo (docs/briefings/2026-10-06-uso-do-sistema-por-modulo.md).
// A gravação é uma linha por pessoa, módulo e dia; a leitura é só do administrador.

// Os portais não têm chave em ROUTE_MODULE_MAP (o acesso a eles vem do perfil),
// mas são uso de verdade. Ficam aqui, e não no mapa, para não mexer em permissão.
export function moduloDoUso(pathname: string): ModuleKey | undefined {
  const modulo = getModuleForPath(pathname);
  if (modulo) return modulo;
  if (pathname === '/portal-colaborador' || pathname.startsWith('/portal-colaborador/')) return 'portal-colaborador';
  if (pathname === '/portal-lider' || pathname.startsWith('/portal-lider/')) return 'portal-lider';
  return undefined;
}

// Só módulos que são tela. `ver-valores-ir`, por exemplo, é chave de permissão
// sem rota: não tem como ser "usado" e só inflaria o "acesso sem uso".
export const MODULOS_RASTREADOS: ModuleKey[] = Array.from(
  new Set<ModuleKey>([...Object.values(ROUTE_MODULE_MAP), 'portal-colaborador', 'portal-lider']),
).sort((a, b) => MODULE_LABELS[a].localeCompare(MODULE_LABELS[b], 'pt-BR'));

function hojeEmBrasilia(agora: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(agora);
}

// Uma chamada por módulo e dia em cada carga da página: o banco já não duplica,
// isto só poupa a rede a cada troca de tela.
const jaRegistrado = new Set<string>();

export function registrarUsoModulo(modulo: string, agora: Date = new Date()): void {
  const chave = `${hojeEmBrasilia(agora)}|${modulo}`;
  if (jaRegistrado.has(chave)) return;
  jaRegistrado.add(chave);
  // Falha aqui nunca chega ao usuário: a tela abre igual.
  Promise.resolve()
    .then(() => supabase.rpc('registrar_uso_modulo', { _modulo: modulo }))
    .then((res) => { if (res?.error) jaRegistrado.delete(chave); })
    .catch(() => { jaRegistrado.delete(chave); });
}

export function limparRegistroDeUsoEmMemoria(): void {
  jaRegistrado.clear();
}

export interface ResumoUsoBruto {
  inicio_coleta: string | null;
  inicio_periodo: string;
  usuarios: { user_id: string; nome: string | null; setor: string | null; papeis: string[]; ultimo_login: string | null; dias_ativos: number }[];
  permissoes: { user_id: string; modulo: string }[];
  uso: { user_id: string; modulo: string; dias: number; ultimo_uso: string }[];
}

export interface LinhaModulo {
  modulo: ModuleKey;
  nome: string;
  usaram: number;
  comAcesso: number;
  adocao: number | null;
  diasDeUso: number;
  ultimoUso: string | null;
}

export interface LinhaPessoa {
  userId: string;
  nome: string;
  setor: string;
  modulosUsados: number;
  modulos: string[];
  diasAtivos: number;
  ultimoUso: string | null;
  ultimoLogin: string | null;
}

export interface LinhaAcessoSemUso {
  userId: string;
  nome: string;
  setor: string;
  modulo: ModuleKey;
  nomeModulo: string;
  ultimoLogin: string | null;
}

export interface ResumoUso {
  inicioColeta: string | null;
  inicioPeriodo: string;
  porModulo: LinhaModulo[];
  porPessoa: LinhaPessoa[];
  acessoSemUso: LinhaAcessoSemUso[];
}

const maisRecente = (a: string | null, b: string | null) => (!a ? b : !b ? a : a > b ? a : b);

export function montarResumoUso(bruto: ResumoUsoBruto): ResumoUso {
  const rastreados = new Set<string>(MODULOS_RASTREADOS);
  const pessoas = new Map(bruto.usuarios.map(u => [u.user_id, u]));
  const permitidos = new Set(bruto.permissoes.map(p => `${p.user_id}|${p.modulo}`));

  // Mesma regra do front: admin vê tudo; os portais vêm do perfil.
  const temAcesso = (userId: string, modulo: ModuleKey): boolean => {
    const papeis = pessoas.get(userId)?.papeis ?? [];
    if (papeis.includes('admin')) return true;
    if (modulo === 'portal-colaborador' && papeis.includes('colaborador')) return true;
    if (modulo === 'portal-lider' && papeis.includes('lider')) return true;
    return permitidos.has(`${userId}|${modulo}`);
  };

  const usoValido = bruto.uso.filter(u => rastreados.has(u.modulo));
  const usou = new Set(usoValido.map(u => `${u.user_id}|${u.modulo}`));

  const porModulo: LinhaModulo[] = MODULOS_RASTREADOS.map(modulo => {
    const linhas = usoValido.filter(u => u.modulo === modulo);
    const comAcesso = bruto.usuarios.filter(u => temAcesso(u.user_id, modulo)).length;
    const usaram = new Set(linhas.map(l => l.user_id)).size;
    return {
      modulo,
      nome: MODULE_LABELS[modulo],
      usaram,
      comAcesso,
      adocao: comAcesso > 0 ? usaram / comAcesso : null,
      diasDeUso: linhas.reduce((s, l) => s + l.dias, 0),
      ultimoUso: linhas.reduce<string | null>((m, l) => maisRecente(m, l.ultimo_uso), null),
    };
  }).sort((a, b) => b.usaram - a.usaram || b.diasDeUso - a.diasDeUso || a.nome.localeCompare(b.nome, 'pt-BR'));

  const porPessoa: LinhaPessoa[] = bruto.usuarios.map(u => {
    const linhas = usoValido.filter(l => l.user_id === u.user_id);
    return {
      userId: u.user_id,
      nome: u.nome || '(sem nome)',
      setor: u.setor || '',
      modulosUsados: linhas.length,
      modulos: linhas
        .slice()
        .sort((a, b) => b.dias - a.dias)
        .map(l => MODULE_LABELS[l.modulo as ModuleKey]),
      // Dias distintos, contados no banco: somar os dias de cada módulo contaria
      // duas vezes o dia em que a pessoa abriu dois módulos.
      diasAtivos: u.dias_ativos ?? 0,
      ultimoUso: linhas.reduce<string | null>((m, l) => maisRecente(m, l.ultimo_uso), null),
      ultimoLogin: u.ultimo_login,
    };
  }).sort((a, b) => b.modulosUsados - a.modulosUsados || b.diasAtivos - a.diasAtivos || a.nome.localeCompare(b.nome, 'pt-BR'));

  // Admin tem acesso a todos os módulos por definição: listá-lo aqui só esconderia
  // o que interessa, que é permissão concedida e não usada.
  const acessoSemUso: LinhaAcessoSemUso[] = [];
  for (const u of bruto.usuarios) {
    if (u.papeis.includes('admin')) continue;
    for (const modulo of MODULOS_RASTREADOS) {
      if (temAcesso(u.user_id, modulo) && !usou.has(`${u.user_id}|${modulo}`)) {
        acessoSemUso.push({
          userId: u.user_id,
          nome: u.nome || '(sem nome)',
          setor: u.setor || '',
          modulo,
          nomeModulo: MODULE_LABELS[modulo],
          ultimoLogin: u.ultimo_login,
        });
      }
    }
  }
  acessoSemUso.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR') || a.nomeModulo.localeCompare(b.nomeModulo, 'pt-BR'));

  return { inicioColeta: bruto.inicio_coleta, inicioPeriodo: bruto.inicio_periodo, porModulo, porPessoa, acessoSemUso };
}
